import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Search, Edit3, Trash2, Book, Layers, HelpCircle, ChevronRight, GripVertical } from 'lucide-react';
import { AnimatePresence, Reorder } from 'framer-motion';

import { adminApi } from '../services/api.jsx';
import ReferentielForm from '../components/forms/ReferentielForm';
import ClasseurReferentiel from '../components/ClasseurReferentiel';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees, Squelette, SqueletteTableau,
} from '../etats';

const NOMS_SECTION = { domaines: 'Domaine', subdomains: 'Sous-domaine', questions: 'Question' };

const TABS = [
  { id: 'domaines', label: 'Domaines', icon: Book, desc: 'Grands axes du diagnostic' },
  { id: 'subdomains', label: 'Sous-domaines', icon: Layers, desc: 'Axes strategiques par domaine' },
  { id: 'questions', label: 'Questions', icon: HelpCircle, desc: 'Questions et reponses (1-5 pts)' },
];

const emptyForm = {
  id: null, nom: '', description: '', domaine_id: '', sous_domaine_id: '', texte: '',
  reponses: [
    { points: 1, label: '' }, { points: 2, label: '' }, { points: 3, label: '' },
    { points: 4, label: '' }, { points: 5, label: '' }
  ]
};

const arraysEqual = (a = [], b = []) => (
  a.length === b.length && a.every((value, idx) => String(value) === String(b[idx]))
);

const reorderByIds = (items = [], orderedIds = []) => {
  const byId = new Map(items.map((item) => [String(item.id), item]));
  return orderedIds.map((id) => byId.get(String(id))).filter(Boolean);
};

export default function Referentiel({ user }) {
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const chargeRef = useRef(false);
  const [erreur, setErreur] = useState(null);
  const [activeTab, setActiveTab] = useState('domaines');
  const [search, setSearch] = useState('');
  const [domaineFilter, setDomaineFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);

  const domainDragStartRef = useRef([]);
  const domainDragLatestRef = useRef([]);
  const subdomainDragStartRef = useRef([]);
  const subdomainDragLatestRef = useRef([]);

  const isReadOnly = user?.role === 'ANALYSTE';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getStructure();
      setStructure(data || []);
      setErreur(null);
      chargeRef.current = true;
      setCharge(true);
    } catch (error) {
      console.error(error);
      // Une actualisation qui échoue garde le référentiel affiché.
      if (chargeRef.current) annoncerErreur(error?.message || 'Le référentiel n’a pas pu être actualisé.');
      else setErreur(error);
    } finally {
      setLoading(false);
    }
  };

  const flatSubdomains = useMemo(() => {
    return structure.flatMap((dom) => (dom.subdomains || []).map((sub) => ({
      ...sub,
      domaine_id: dom.id,
      domaine_nom: dom.nom
    })));
  }, [structure]);

  const flatQuestions = useMemo(() => {
    return structure.flatMap((dom) =>
      (dom.subdomains || []).flatMap((sub) =>
        (sub.questions || []).map((q) => ({
          ...q,
          domaine_id: dom.id,
          domaine_nom: dom.nom,
          sous_domaine_id: sub.id,
          sous_domaine_nom: sub.nom
        }))
      )
    );
  }, [structure]);

  const filteredData = useMemo(() => {
    const s = search.toLowerCase().trim();
    const hasDomainFilter = Boolean(domaineFilter);

    if (!s) {
      if (activeTab === 'domaines') return structure;
      if (activeTab === 'subdomains') {
        return hasDomainFilter
          ? flatSubdomains.filter((sub) => String(sub.domaine_id) === String(domaineFilter))
          : flatSubdomains;
      }
      if (activeTab === 'questions') {
        return hasDomainFilter
          ? flatQuestions.filter((q) => String(q.domaine_id) === String(domaineFilter))
          : flatQuestions;
      }
    }

    if (activeTab === 'domaines') {
      return structure.filter((d) => d.nom?.toLowerCase().includes(s) || d.id?.toLowerCase().includes(s));
    }

    if (activeTab === 'subdomains') {
      return flatSubdomains.filter((sub) => {
        const matchesDomain = !hasDomainFilter || String(sub.domaine_id) === String(domaineFilter);
        const matchesSearch = sub.nom?.toLowerCase().includes(s)
          || sub.domaine_nom?.toLowerCase().includes(s)
          || sub.id?.toLowerCase().includes(s);
        return matchesDomain && matchesSearch;
      });
    }

    if (activeTab === 'questions') {
      return flatQuestions.filter((q) => {
        const matchesDomain = !hasDomainFilter || String(q.domaine_id) === String(domaineFilter);
        const matchesSearch = q.texte?.toLowerCase().includes(s)
          || q.sous_domaine_nom?.toLowerCase().includes(s)
          || q.domaine_nom?.toLowerCase().includes(s)
          || q.id?.toLowerCase().includes(s);
        return matchesDomain && matchesSearch;
      });
    }

    return [];
  }, [structure, flatSubdomains, flatQuestions, activeTab, search, domaineFilter]);

  const selectedDomain = useMemo(
    () => structure.find((dom) => String(dom.id) === String(domaineFilter)),
    [structure, domaineFilter]
  );

  const domainById = useMemo(
    () => Object.fromEntries(structure.map((dom) => [String(dom.id), dom])),
    [structure]
  );
  const subdomainById = useMemo(
    () => Object.fromEntries(((selectedDomain?.subdomains) || []).map((sub) => [String(sub.id), sub])),
    [selectedDomain]
  );

  const domainReorderEnabled = !isReadOnly && activeTab === 'domaines' && !search.trim();
  const subdomainReorderEnabled = !isReadOnly
    && activeTab === 'subdomains'
    && Boolean(domaineFilter)
    && !search.trim();

  const domainReorderIds = useMemo(
    () => (domainReorderEnabled ? structure.map((dom) => String(dom.id)) : []),
    [domainReorderEnabled, structure]
  );

  const subdomainReorderIds = useMemo(
    () => (
      subdomainReorderEnabled
        ? ((selectedDomain?.subdomains || []).map((sub) => String(sub.id)))
        : []
    ),
    [subdomainReorderEnabled, selectedDomain]
  );

  const handleOpenModal = (entity = null) => {
    if (isReadOnly) return;
    if (entity) {
      setFormData({
        id: entity.id,
        nom: entity.nom ?? '',
        description: entity.description ?? '',
        domaine_id: entity.domaine_id ?? entity.domaine?.id ?? '',
        sous_domaine_id: entity.sous_domaine_id ?? entity.sous_domaine?.id ?? '',
        texte: entity.texte ?? '',
        reponses: (entity.reponses && entity.reponses.length)
          ? entity.reponses.map((r) => ({ points: r.points, label: r.label ?? '' }))
          : emptyForm.reponses
      });
    } else {
      setFormData({ ...emptyForm, domaine_id: structure[0]?.id ?? '', sous_domaine_id: '' });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = activeTab === 'domaines'
        ? { id: formData.id, nom: formData.nom }
        : activeTab === 'subdomains'
          ? { id: formData.id, nom: formData.nom, description: formData.description, domaine_id: formData.domaine_id }
          : { id: formData.id, texte: formData.texte, sous_domaine_id: formData.sous_domaine_id, reponses: formData.reponses };
      await adminApi.saveEntity(activeTab, payload);
      setShowModal(false);
      annoncerSucces(`${NOMS_SECTION[activeTab] || 'Élément'} ${formData.id ? 'modifié' : 'ajouté'}.`);
      loadData();
    } catch (err) {
      annoncerErreur(err?.message || "Erreur d'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (isReadOnly || !await confirmer('Supprimer definitivement cet element ?')) return;
    try {
      await adminApi.deleteEntity(activeTab, id);
      annoncerSucces(`${NOMS_SECTION[activeTab] || 'Élément'} supprimé.`);
      loadData();
    } catch (err) {
      annoncerErreur(err?.message || 'Erreur lors de la suppression.');
    }
  };

  // Sauvegarde centralisee de l'ordre des domaines apres drop.
  const persistDomainOrder = async (orderedIds) => {
    if (!orderedIds?.length) return;
    try {
      setOrderSaving(true);
      await adminApi.reorderDomaines(orderedIds);
      annoncerSucces('Ordre des domaines enregistré.');
    } catch (err) {
      annoncerErreur(err?.message || "Impossible d'enregistrer l'ordre des domaines.");
      loadData();
    } finally {
      setOrderSaving(false);
    }
  };

  // Sauvegarde centralisee de l'ordre des sous-domaines apres drop.
  const persistSubdomainOrder = async (domaineId, orderedIds) => {
    if (!domaineId || !orderedIds?.length) return;
    try {
      setOrderSaving(true);
      await adminApi.reorderSousDomaines(domaineId, orderedIds);
      annoncerSucces('Ordre des sous-domaines enregistré.');
    } catch (err) {
      annoncerErreur(err?.message || "Impossible d'enregistrer l'ordre des sous-domaines.");
      loadData();
    } finally {
      setOrderSaving(false);
    }
  };

  // Reordonner les domaines localement pour un feedback visuel immediat.
  const handleDomainReorder = (orderedIds) => {
    domainDragLatestRef.current = orderedIds;
    setStructure((prev) => reorderByIds(prev, orderedIds));
  };

  const handleDomainDragStart = () => {
    const startOrder = structure.map((dom) => String(dom.id));
    domainDragStartRef.current = startOrder;
    domainDragLatestRef.current = startOrder;
  };

  const handleDomainDragEnd = () => {
    const fallbackOrder = structure.map((dom) => String(dom.id));
    const finalOrder = domainDragLatestRef.current.length ? domainDragLatestRef.current : fallbackOrder;
    if (arraysEqual(domainDragStartRef.current, finalOrder)) return;
    persistDomainOrder(finalOrder);
  };

  // Reordonner les sous-domaines localement dans le domaine selectionne.
  const handleSubdomainReorder = (orderedIds) => {
    subdomainDragLatestRef.current = orderedIds;
    setStructure((prev) => prev.map((dom) => {
      if (String(dom.id) !== String(domaineFilter)) return dom;
      return { ...dom, subdomains: reorderByIds(dom.subdomains || [], orderedIds) };
    }));
  };

  const handleSubdomainDragStart = () => {
    const startOrder = (selectedDomain?.subdomains || []).map((sub) => String(sub.id));
    subdomainDragStartRef.current = startOrder;
    subdomainDragLatestRef.current = startOrder;
  };

  const handleSubdomainDragEnd = () => {
    if (!domaineFilter) return;
    const fallbackOrder = (selectedDomain?.subdomains || []).map((sub) => String(sub.id));
    const finalOrder = subdomainDragLatestRef.current.length ? subdomainDragLatestRef.current : fallbackOrder;
    if (arraysEqual(subdomainDragStartRef.current, finalOrder)) return;
    persistSubdomainOrder(domaineFilter, finalOrder);
  };

  // Premier chargement seulement : après une action, la table reste affichée.
  if (loading && !charge) {
    return (
      <div className="space-y-6">
        <div>
          <Squelette className="h-8 w-56 mb-3" />
          <Squelette className="h-3 w-80 max-w-full" />
        </div>
        <Squelette className="h-11 w-96 max-w-full" />
        <SqueletteTableau colonnes={3} />
      </div>
    );
  }

  if (erreur && !charge) {
    return (
      <div className="space-y-6">
        <h1 className="titre-page">Référentiel</h1>
        <div className="carte-tableau"><EtatErreur erreur={erreur} onReessayer={loadData} /></div>
      </div>
    );
  }

  const renderDomainCells = (item, index, draggable = false) => (
    <>
      <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)]">
        <div className="flex items-center gap-2">
          {draggable && <GripVertical className="w-3.5 h-3.5 text-[var(--text-muted)]" />}
          <span>{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4 font-bold text-[var(--text-bold)] group-hover:text-[var(--accent-texte)]">{item.nom}</td>
      <td className="px-6 py-4 text-right">
        {!isReadOnly && (
          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
            <button type="button" onClick={() => handleOpenModal(item)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
            <button type="button" onClick={() => handleDelete(item.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </td>
    </>
  );

  const renderSubdomainCells = (item, index, draggable = false) => (
    <>
      <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)]">
        <div className="flex items-center gap-2">
          {draggable && <GripVertical className="w-3.5 h-3.5 text-[var(--text-muted)]" />}
          <span>{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4 font-bold text-[var(--text-bold)]">{item.nom}</td>
      <td className="px-6 py-4 text-sm text-[var(--text-muted)] max-w-[240px] truncate" title={item.description}>{item.description || '-'}</td>
      <td className="px-6 py-4 text-xs text-[var(--text-muted)] flex items-center gap-1"><ChevronRight className="w-3 h-3" /> {item.domaine_nom}</td>
      <td className="px-6 py-4 text-right">
        {!isReadOnly && (
          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
            <button type="button" onClick={() => handleOpenModal(item)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
            <button type="button" onClick={() => handleDelete(item.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </td>
    </>
  );

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="titre-page">Référentiel</h1>
          <p className="text-[var(--text-muted)] font-medium mt-1">Matrice technique : Domaines {'->'} Sous-domaines {'->'} Questions.</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <ClasseurReferentiel peutTeleverser={!isReadOnly} onApplique={loadData} />
          {!isReadOnly && (
            <button type="button" onClick={() => handleOpenModal()} className="act">
              <Plus className="w-4 h-4" /> Nouveau
            </button>
          )}
        </div>
      </div>

      <div className="segmente max-w-full overflow-x-auto" role="group" aria-label="Sections du référentiel">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setActiveTab(t.id);
              if (t.id === 'domaines') setDomaineFilter('');
            }}
            aria-pressed={activeTab === t.id}
            className="whitespace-nowrap shrink-0"
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher..."
            className="champ champ-loupe font-medium"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {(activeTab === 'subdomains' || activeTab === 'questions') && (
          <div className="min-w-[220px]">
            <select
              className="filtre"
              value={domaineFilter}
              onChange={(e) => setDomaineFilter(e.target.value)}
            >
              <option value="">Tous les domaines</option>
              {structure.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
          </div>
        )}

        <span className="text-[10px] font-semibold uppercase text-[var(--text-muted)] tracking-widest">{filteredData.length} element(s)</span>

        {!isReadOnly && activeTab === 'domaines' && !search.trim() && (
          <span className="text-[10px] font-semibold uppercase text-[var(--text-muted)] tracking-widest">Glissez-deposez les lignes pour reordonner</span>
        )}

        {!isReadOnly && activeTab === 'subdomains' && !search.trim() && !domaineFilter && (
          <span className="text-[10px] font-semibold uppercase text-[var(--text-muted)] tracking-widest">Selectionnez un domaine pour reordonner ses sous-domaines</span>
        )}

        {orderSaving && (
          <span className="text-[10px] font-semibold uppercase text-[var(--accent-texte)] tracking-widest">Sauvegarde de l'ordre...</span>
        )}
      </div>

      <div className="carte-tableau">
        <div className="tableau-hote">
          <table className="w-full text-left min-w-[640px]">
            <thead className="">
              <tr>
                <th className="w-16">No</th>
                {activeTab === 'domaines' && <th className="">Nom</th>}
                {activeTab === 'subdomains' && (
                  <>
                    <th className="">Nom</th>
                    <th className="">Description</th>
                    <th className="">Domaine parent</th>
                  </>
                )}
                {activeTab === 'questions' && (
                  <>
                    <th className="">Texte</th>
                    <th className="">Axe / Domaine</th>
                    <th className="">Reponses (pts)</th>
                  </>
                )}
                <th className="text-right w-28">Actions</th>
              </tr>
            </thead>

            {activeTab === 'domaines' && domainReorderEnabled && filteredData.length > 0 && (
              <Reorder.Group as="tbody" axis="y" values={domainReorderIds} onReorder={handleDomainReorder} layoutScroll className="divide-y divide-[var(--border)]">
                {domainReorderIds.map((id, index) => {
                  const item = domainById[String(id)];
                  if (!item) return null;
                  return (
                    <Reorder.Item
                      as="tr"
                      key={item.id}
                      value={String(item.id)}
                      className="group cursor-grab active:cursor-grabbing"
                      layout
                      transition={{ type: 'spring', stiffness: 280, damping: 28, mass: 0.6 }}
                      dragElastic={0.06}
                      dragMomentum={false}
                      dragTransition={{ bounceStiffness: 350, bounceDamping: 35 }}
                      onDragStart={handleDomainDragStart}
                      onDragEnd={handleDomainDragEnd}
                      whileDrag={{
                        scale: 1.015,
                        boxShadow: '0 8px 32px rgba(10,17,40,0.22)',
                        backgroundColor: 'rgba(94,189,79,0.07)',
                        zIndex: 50,
                      }}
                    >
                      {renderDomainCells(item, index, true)}
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            )}

            {activeTab === 'subdomains' && subdomainReorderEnabled && filteredData.length > 0 && (
              <Reorder.Group as="tbody" axis="y" values={subdomainReorderIds} onReorder={handleSubdomainReorder} layoutScroll className="divide-y divide-[var(--border)]">
                {subdomainReorderIds.map((id, index) => {
                  const item = subdomainById[String(id)];
                  if (!item) return null;
                  const enriched = { ...item, domaine_nom: selectedDomain?.nom, domaine_id: selectedDomain?.id };
                  return (
                    <Reorder.Item
                      as="tr"
                      key={item.id}
                      value={String(item.id)}
                      className="group cursor-grab active:cursor-grabbing"
                      layout
                      transition={{ type: 'spring', stiffness: 280, damping: 28, mass: 0.6 }}
                      dragElastic={0.06}
                      dragMomentum={false}
                      dragTransition={{ bounceStiffness: 350, bounceDamping: 35 }}
                      onDragStart={handleSubdomainDragStart}
                      onDragEnd={handleSubdomainDragEnd}
                      whileDrag={{
                        scale: 1.015,
                        boxShadow: '0 8px 32px rgba(10,17,40,0.22)',
                        backgroundColor: 'rgba(94,189,79,0.07)',
                        zIndex: 50,
                      }}
                    >
                      {renderSubdomainCells(enriched, index, true)}
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            )}

            {!(activeTab === 'domaines' && domainReorderEnabled && filteredData.length > 0)
              && !(activeTab === 'subdomains' && subdomainReorderEnabled && filteredData.length > 0)
              && (
                <tbody className="divide-y divide-[var(--border)]">
                  {filteredData.length === 0 && (
                    <tr>
                      <td colSpan={activeTab === 'domaines' ? 3 : 5}>
                        {search.trim() ? (
                          <EtatSansDonnees cas="sansResultat" compact titre={`Aucun résultat pour « ${search.trim()} »`}
                            action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
                        ) : domaineFilter && activeTab !== 'domaines' ? (
                          <EtatSansDonnees cas="sansFiltre" compact titre="Rien dans ce domaine"
                            action={<button type="button" className="act act-sm act-contour" onClick={() => setDomaineFilter('')}>Réinitialiser</button>} />
                        ) : (
                          <EtatSansDonnees cas="vide" compact titre="Aucun élément"
                            aide={isReadOnly ? 'Le référentiel est encore vide.' : 'Ajoutez le premier élément de cette section.'}
                            action={!isReadOnly && (
                              <button type="button" onClick={() => handleOpenModal()} className="act act-sm">
                                <Plus className="w-4 h-4" /> Nouveau
                              </button>
                            )} />
                        )}
                      </td>
                    </tr>
                  )}

                  {activeTab === 'domaines' && filteredData.map((item, index) => (
                    <tr key={item.id} className="hover:bg-[var(--bg-main)] group transition-all">
                      {renderDomainCells(item, index)}
                    </tr>
                  ))}

                  {activeTab === 'subdomains' && filteredData.map((item, index) => (
                    <tr key={item.id} className="hover:bg-[var(--bg-main)] group transition-all">
                      {renderSubdomainCells(item, index)}
                    </tr>
                  ))}

                  {activeTab === 'questions' && filteredData.map((item, index) => (
                    <tr key={item.id} className="hover:bg-[var(--bg-main)] group transition-all">
                      <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)] align-top">{index + 1}</td>
                      <td className="px-6 py-4 text-sm font-medium text-[var(--text-bold)] max-w-[320px] align-top">{item.texte || '-'}</td>
                      <td className="px-6 py-4 text-xs text-[var(--text-muted)] align-top">
                        <span className="block font-medium text-[var(--text-muted)]">{item.sous_domaine_nom}</span>
                        <span className="flex items-center gap-1 mt-0.5"><ChevronRight className="w-3 h-3" /> {item.domaine_nom}</span>
                      </td>
                      <td className="px-6 py-4 text-xs text-[var(--text-muted)] align-top">
                        {item.reponses?.length
                          ? item.reponses.map((r) => `${r.points}: ${r.label?.slice(0, 12)}${(r.label?.length || 0) > 12 ? '...' : ''}`).join(' | ')
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-right align-top">
                        {!isReadOnly && (
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button type="button" onClick={() => handleOpenModal(item)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
                            <button type="button" onClick={() => handleDelete(item.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <ReferentielForm
            activeTab={activeTab}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSave}
            onClose={() => setShowModal(false)}
            saving={saving}
            structure={structure}
            tabs={TABS}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
